import React, { useState, useEffect, useContext, useCallback } from 'react';
import axiosInstance from '../axiosConfig';
import { buildMediaUrl } from '../apiConfig';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { AuthContext } from '../AuthContext';
import { Spinner, Form, Button, Table, OverlayTrigger, Tooltip, Modal } from 'react-bootstrap';
import Select from 'react-select';

function PurchaseOrderConstructorPage({ onSaveSuccess, onSelectOrder }) {
    const { authTokens } = useContext(AuthContext);
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState('');
    const [compatibilities, setCompatibilities] = useState([]);
    const [selectedCompatibility, setSelectedCompatibility] = useState('');
    const [availableComponents, setAvailableComponents] = useState([]);
    const [selectedComponents, setSelectedComponents] = useState([]);
    const [orderName, setOrderName] = useState('');
    const [orders, setOrders] = useState([]);
    const [editingOrderId, setEditingOrderId] = useState(null);
    const [showOrders, setShowOrders] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [modalImage, setModalImage] = useState('');

    // Оборачиваем функции в useCallback
    const fetchProjects = useCallback(() => {
        axiosInstance.get('/api/projects/', {
            headers: { 'Authorization': `Bearer ${authTokens.access}` }
        })
        .then(response => setProjects(response.data.projects || []))
        .catch(error => console.error('Ошибка при загрузке проектов:', error));
    }, [authTokens.access]);

    const fetchOrders = useCallback(() => {
        axiosInstance.get('/api/purchase-order/list/', {
            headers: { 'Authorization': `Bearer ${authTokens.access}` }
        })
        .then(response => setOrders(response.data.orders || []))
        .catch(error => console.error('Ошибка при загрузке заказов:', error));
    }, [authTokens.access]);

    useEffect(() => {
        fetchProjects();
        fetchOrders();
    }, [fetchProjects, fetchOrders]); // Добавляем функции в массив зависимостей

    const fetchCompatibilities = useCallback((projectId) => {
        axiosInstance.get(`/api/compatibilities/${projectId}/`, {
            headers: { 'Authorization': `Bearer ${authTokens.access}` }
        })
        .then(response => setCompatibilities(response.data.compatibilities || []))
        .catch(error => console.error('Ошибка при загрузке совместимостей:', error));
    }, [authTokens.access]);

    const fetchComponentDetails = useCallback((componentId) => {
        return axiosInstance.get(`/api/component/${componentId}/`, {
            headers: { 'Authorization': `Bearer ${authTokens.access}` }
        })
        .then(response => response.data)
        .catch(error => {
            console.error('Ошибка при загрузке деталей компонента:', error);
            return null;
        });
    }, [authTokens.access]);

    const fetchComponents = useCallback((projectId, compatibilityId) => {
        if (!projectId || !compatibilityId) return;
        setLoading(true);
        axiosInstance.get(`/api/components/${projectId}/${compatibilityId}/`, {
            headers: { 'Authorization': `Bearer ${authTokens.access}` }
        })
        .then(response => {
            const componentPromises = response.data.components.map(comp => fetchComponentDetails(comp.id));
            Promise.all(componentPromises).then(data => {
                const filteredComponents = data.filter(component => 
                    !selectedComponents.some(selectedComponent => selectedComponent.id === component?.id)
                );
                setAvailableComponents(filteredComponents.filter(component => component !== null));
            });
        })
        .catch(error => console.error('Ошибка при загрузке компонентов:', error))
        .finally(() => setLoading(false));
    }, [authTokens.access, fetchComponentDetails, selectedComponents]);

    const handleProjectChange = (selectedOption) => {
        const projectId = selectedOption ? selectedOption.value : '';
        setSelectedProject(projectId);
        fetchCompatibilities(projectId);
    };

    const handleCompatibilityChange = (selectedOption) => {
        const compatibilityId = selectedOption ? selectedOption.value : '';
        setSelectedCompatibility(compatibilityId);
        fetchComponents(selectedProject, compatibilityId);
    };

    const handleDragEnd = (result) => {
        const { source, destination } = result;
        if (!destination) return;

        if (source.droppableId === destination.droppableId && source.index === destination.index) return;

        try {
            if (source.droppableId === 'availableComponents' && destination.droppableId === 'selectedComponents') {
                const movedComponent = availableComponents[source.index];
                setSelectedComponents([...selectedComponents, { ...movedComponent, orderQuantity: 1 }]);
                setAvailableComponents(availableComponents.filter((_, idx) => idx !== source.index));
            }

            if (source.droppableId === 'selectedComponents' && destination.droppableId === 'availableComponents') {
                const movedComponent = selectedComponents[source.index];
                setAvailableComponents([...availableComponents, movedComponent]);
                setSelectedComponents(selectedComponents.filter((_, idx) => idx !== source.index));
            }
        } catch (error) {
            console.error('Error during drag operation:', error);
            window.Toastify({
                text: "Произошла ошибка при перетаскивании компонентов.",
                duration: 3000,
                gravity: "top",
                position: "right",
                backgroundColor: "#ff0000"
            }).showToast();
        }
    };

    const handleDoubleClick = (index, list) => {
        if (list === 'available') {
            handleAddComponent(index);
        } else if (list === 'selected') {
            handleRemoveComponent(index);
        }
    };

    const handleAddComponent = (index) => {
        const movedComponent = availableComponents[index];
        setSelectedComponents([...selectedComponents, { ...movedComponent, orderQuantity: 1 }]);
        setAvailableComponents(availableComponents.filter((_, idx) => idx !== index));
    };

    const handleRemoveComponent = (index) => {
        const movedComponent = selectedComponents[index];
        setAvailableComponents([...availableComponents, movedComponent]);
        setSelectedComponents(selectedComponents.filter((_, idx) => idx !== index));
    };

    const handleQuantityChange = (index, quantity) => {
        const updatedComponents = [...selectedComponents];
        updatedComponents[index].orderQuantity = quantity;
        setSelectedComponents(updatedComponents);
    };

    const handleSaveOrder = () => {
        if (selectedComponents.length === 0) {
            window.Toastify({
                text: "Нет компонентов для добавления в заказ.",
                duration: 3000,
                gravity: "top",
                position: "right",
                backgroundColor: "#ff0000"
            }).showToast();
            return;
        }

        if (!orderName.trim()) {
            window.Toastify({
                text: "Пожалуйста, введите имя заказа.",
                duration: 3000,
                gravity: "top",
                position: "right",
                backgroundColor: "#ff0000"
            }).showToast();
            return;
        }

        if (editingOrderId) {
            const addItemPromises = selectedComponents.map(({ id, orderQuantity }) =>
                axiosInstance.post(`/api/purchase-order/${editingOrderId}/add-item/`, {
                    component_id: id,
                    quantity: orderQuantity
                }, {
                    headers: { 'Authorization': `Bearer ${authTokens.access}` }
                })
            );

            Promise.all(addItemPromises)
            .then(() => {
                onSaveSuccess();
                fetchOrders();
            })
            .catch(error => {
                console.error('Ошибка при добавлении компонентов в заказ:', error);
                window.Toastify({
                    text: "Ошибка при добавлении компонентов в заказ.",
                    duration: 3000,
                    gravity: "top",
                    position: "right",
                    backgroundColor: "#ff0000"
                }).showToast();
            });
        } else {
            axiosInstance.post('/api/purchase-order/create/', { order_name: orderName }, {
                headers: { 'Authorization': `Bearer ${authTokens.access}` }
            })
            .then(response => {
                const orderId = response.data.order_id;

                const addItemPromises = selectedComponents.map(({ id, orderQuantity }) =>
                    axiosInstance.post(`/api/purchase-order/${orderId}/add-item/`, {
                        component_id: id,
                        quantity: orderQuantity
                    }, {
                        headers: { 'Authorization': `Bearer ${authTokens.access}` }
                    })
                );

                return Promise.all(addItemPromises).then(() => orderId);
            })
            .then(orderId => {
                onSelectOrder(orderId);
                fetchOrders();
            })
            .catch(error => {
                console.error('Ошибка при добавлении компонентов в заказ:', error);
                window.Toastify({
                    text: "Ошибка при добавлении компонентов в заказ.",
                    duration: 3000,
                    gravity: "top",
                    position: "right",
                    backgroundColor: "#ff0000"
                }).showToast();
            });
        }
    };

    const handleOrderClick = (orderId) => {
        onSelectOrder(orderId);
    };

    const handleEditOrder = (orderId, event) => {
        event.stopPropagation();
        axiosInstance.get(`/api/purchase-order/${orderId}/details/`, {
            headers: { 'Authorization': `Bearer ${authTokens.access}` }
        })
        .then(response => {
            const orderDetails = response.data;
            setOrderName(orderDetails.order_name);
            setSelectedComponents(orderDetails.items.map(item => ({
                id: item.component.id,
                name: item.component.name,
                orderQuantity: item.quantity,
                photo: item.component.photo || '',
                batch_number: item.component.batch_number || 'N/A'
            })));
            setEditingOrderId(orderId);
        })
        .catch(error => console.error('Ошибка при загрузке деталей заказа:', error));
    };

    const handleShowModal = (imageUrl) => {
        setModalImage(imageUrl);
        setShowModal(true);
    };

    const renderCellWithTooltip = (text) => (
        <OverlayTrigger
            placement="top"
            overlay={<Tooltip>{text}</Tooltip>}
        >
            <span className="text-truncate" style={{ maxWidth: '150px', display: 'inline-block', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {text}
            </span>
        </OverlayTrigger>
    );

    const selectStyles = {
        container: (provided) => ({
            ...provided,
            width: '100%',
        }),
        control: (provided) => ({
            ...provided,
            minHeight: '46px',
            padding: 0,
            borderRadius: '14px',
            display: 'flex',
            alignItems: 'center',
            borderColor: 'var(--warehouse-border)',
            backgroundColor: 'rgba(255, 255, 255, 0.88)',
            boxShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.78), 0 4px 14px rgba(15, 23, 42, 0.04)',
        }),
        menu: (provided) => ({
            ...provided,
            zIndex: 9999,
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 18px 36px rgba(15, 23, 42, 0.12)',
        }),
        singleValue: (provided) => ({
            ...provided,
            color: 'var(--warehouse-text-strong)',
        }),
        valueContainer: (provided) => ({
            ...provided,
            minHeight: '44px',
            padding: '0 14px',
        }),
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0,
            color: 'var(--warehouse-text-strong)',
        }),
        placeholder: (provided) => ({
            ...provided,
            color: 'var(--warehouse-text-muted)',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            color: 'rgba(100, 116, 139, 0.8)',
            padding: '0 14px',
        }),
        indicatorSeparator: () => ({
            display: 'none',
        }),
        option: (provided, state) => ({
            ...provided,
            color: 'var(--warehouse-text-strong)',
            backgroundColor: state.isFocused ? 'rgba(204, 251, 241, 0.58)' : '#fff',
        }),
    };

    return (
        <div className="purchase-order-constructor container">
            <h3>{editingOrderId ? 'Редактировать заказ' : 'Создать заказ на закупку'}</h3>
            <Form.Group controlId="orderNameInput" className="mb-3">
                <Form.Label>Имя заказа</Form.Label>
                <Form.Control
                    type="text"
                    placeholder="Введите имя заказа"
                    value={orderName}
                    onChange={(e) => setOrderName(e.target.value)}
                />
            </Form.Group>

            <div className="row mb-4">
                <div className="col-md-6">
                    <Form.Group controlId="projectSelect">
                        <Form.Label>Выберите проект</Form.Label>
                        <Select
                            value={projects.find(project => project.value === selectedProject)}
                            options={projects.map(project => ({
                                value: project.id,
                                label: project.name
                            }))}
                            onChange={handleProjectChange}
                            styles={selectStyles}
                            classNamePrefix="warehouse-select"
                            isSearchable
                            isClearable
                        />
                    </Form.Group>
                </div>

                {selectedProject && (
                    <div className="col-md-6">
                        <Form.Group controlId="compatibilitySelect">
                            <Form.Label>Выберите совместимость</Form.Label>
                            <Select
                                value={compatibilities.find(compat => compat.value === selectedCompatibility)}
                                options={compatibilities.map(compat => ({
                                    value: compat.id,
                                    label: compat.name
                                }))}
                                onChange={handleCompatibilityChange}
                                styles={selectStyles}
                                classNamePrefix="warehouse-select"
                                isSearchable
                                isClearable
                            />
                        </Form.Group>
                    </div>
                )}
            </div>

            {loading ? (
                <Spinner animation="border" />
            ) : (
                <DragDropContext onDragEnd={handleDragEnd}>
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <h5>Доступные компоненты</h5>
                            <Droppable droppableId="availableComponents">
                                {(provided) => (
                                    <Table ref={provided.innerRef} {...provided.droppableProps} striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Фото</th>
                                                <th>Название</th>
                                                <th>На складе</th>
                                                <th>Партия</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        {availableComponents.map((component, index) => (
                                            <Draggable key={component?.id?.toString()} draggableId={component?.id?.toString()} index={index}>
                                                {(provided) => (
                                                    <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                                        onDoubleClick={() => handleDoubleClick(index, 'available')}>
                                                        <td>
                                                            <img 
                                                                src={buildMediaUrl(component?.photo)} 
                                                                alt={component?.name} 
                                                                style={{ width: '50px', cursor: 'pointer' }} 
                                                                onClick={() => handleShowModal(buildMediaUrl(component?.photo))}
                                                            />
                                                        </td>
                                                        <td>{renderCellWithTooltip(component?.name)}</td>
                                                        <td>{component?.quantity}</td>
                                                        <td>{renderCellWithTooltip(component?.batch_number || 'N/A')}</td>
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                        </tbody>
                                    </Table>
                                )}
                            </Droppable>
                        </div>

                        <div className="col-md-6">
                            <h5>Выбранные компоненты</h5>
                            <Droppable droppableId="selectedComponents">
                                {(provided) => (
                                    <Table ref={provided.innerRef} {...provided.droppableProps} striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Фото</th>
                                                <th>Название</th>
                                                <th>Количество</th>
                                                <th>Партия</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                        {selectedComponents.map((component, index) => (
                                            <Draggable key={component?.id?.toString()} draggableId={component?.id?.toString()} index={index}>
                                                {(provided) => (
                                                    <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}
                                                        onDoubleClick={() => handleDoubleClick(index, 'selected')}>
                                                        <td>
                                                            <img 
                                                                src={buildMediaUrl(component?.photo)} 
                                                                alt={component?.name} 
                                                                style={{ width: '50px', cursor: 'pointer' }} 
                                                                onClick={() => handleShowModal(buildMediaUrl(component?.photo))}
                                                            />
                                                        </td>
                                                        <td>{renderCellWithTooltip(component?.name)}</td>
                                                        <td>
                                                            <Form.Control
                                                                type="number"
                                                                min="1"
                                                                value={component.orderQuantity}
                                                                onChange={(e) => handleQuantityChange(index, parseInt(e.target.value, 10))}
                                                            />
                                                        </td>
                                                        <td>{renderCellWithTooltip(component?.batch_number)}</td>
                                                    </tr>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                        </tbody>
                                    </Table>
                                )}
                            </Droppable>
                        </div>
                    </div>
                </DragDropContext>
            )}

            <Button variant="primary" onClick={handleSaveOrder} className="mt-3 mb-3">
                {editingOrderId ? (
                    <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-save-changes">Сохранить изменения</Tooltip>}>
                        <i className="fas fa-save"></i>
                    </OverlayTrigger>
                ) : (
                    <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-save-order">Сохранить заказ</Tooltip>}>
                        <i className="fas fa-save"></i>
                    </OverlayTrigger>
                )}
            </Button>

            <h4 onClick={() => setShowOrders(!showOrders)} style={{ cursor: 'pointer', marginBottom: '15px' }}>
                {showOrders ? (
                    <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-hide-orders">Скрыть заказы</Tooltip>}>
                        <i className="fas fa-chevron-up"></i>
                    </OverlayTrigger>
                ) : (
                    <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-show-orders">Показать заказы</Tooltip>}>
                        <i className="fas fa-chevron-down"></i>
                    </OverlayTrigger>
                )} {' Сохранённые заказы'}
            </h4>

            {showOrders && (
                <div className="saved-orders mt-4">
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>ID Заказа</th>
                                <th>Дата создания</th>
                                <th>Имя заказа</th>
                                <th>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orders.map(order => (
                                <tr key={order.id} onClick={() => handleOrderClick(order.id)}>
                                    <td>{order.id}</td>
                                    <td>{order.date_created}</td>
                                    <td>{order.order_name}</td>
                                    <td>
                                        <OverlayTrigger placement="top" overlay={<Tooltip id="tooltip-edit-order">Редактировать</Tooltip>}>
                                            <Button variant="secondary" onClick={(event) => handleEditOrder(order.id, event)}>
                                                <i className="fas fa-edit"></i>
                                            </Button>
                                        </OverlayTrigger>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>
            )}

            {/* Модальное окно для увеличенного изображения */}
            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
                className="warehouse-modal warehouse-media-modal"
                dialogClassName="warehouse-modal-dialog warehouse-modal-dialog--lg"
                contentClassName="warehouse-modal-content"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Увеличенное изображение</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <img src={modalImage} alt="component" style={{ width: '100%' }} />
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)}>
                        Закрыть
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default PurchaseOrderConstructorPage;
